import { useEffect, useState } from 'react';
import { DAY_MILLIS } from '../../../constants';
import { EsclationStatusResponse, Ticket } from '../../../types/services.type';
import EsclationForm from './esclationForm';
import { toast } from 'react-toastify';
import { Box, Button, TextField } from '@mui/material';
import TrackStatusService from '../../../services/getStatus.service';
import EsclationResponseMessage from './esclationResponseMessage';
import EscalationStatusMessage from './escalationStatusMessage';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import AuthServices from '../../../services/auth.services';
import { loginSuccess } from '../../../store/Slices/authSlice';
import ActivityIndicator from '../../../components/activityIndicator';
import HeadingWithDescription from '../../../components/headingWithDescription';

function OTPForm({
  number,
  handleRequestPrioritySupport,
  setOtpSent,
}: {
  number: string;
  handleRequestPrioritySupport: any;
  setOtpSent: any;
}) {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const handleVerifyOTP = async () => {
    try {
      setLoading(true);
      if (otp?.trim()?.length !== 6) return toast.warn('Invalid OTP');
      const token = await AuthServices.verifyOTP(number, otp);
      if (token) {
        dispatch(loginSuccess({ userId: number, token: token }));
        toast?.success('User verified. Please continue.');
        setOtpSent(false);
        handleRequestPrioritySupport();
      } else {
        toast.error('Could not verify the OTP');
      }
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <ActivityIndicator />}
      <HeadingWithDescription
        heading=""
        description={`We have sent OTP to ${number}. Please verify to continue.`}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          fullWidth
          label="Enter OTP"
          variant="outlined"
          value={otp}
          onSubmit={handleVerifyOTP}
          onChange={(event) => setOtp(event?.target?.value)}
        />
        <Button
          disabled={otp?.trim()?.length !== 6}
          onClick={handleVerifyOTP}
          variant="contained"
          fullWidth
          size="large"
        >
          VERIFY
        </Button>
      </Box>
    </>
  );
}

export default function EsclateIssueButton({
  reportedOn,
  ticket,
}: {
  reportedOn: string;
  ticket: Ticket;
}) {
  const { isAuthenticated, token } = useSelector((state: RootState) => state.auth);
  const userId = useSelector((state: RootState) => state.auth.userId);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [esclationStatus, setEsclationStatus] = useState<EsclationStatusResponse | null>(null);
  const [isOtpSent, setOtpSent] = useState<boolean>(false);

  const getEsclationStatus = async () => {
    if (!token) return;
    try {
      setLoading(true);
      let res = await TrackStatusService.getEscalationStatusAsync(ticket?.ticketNo, token);
      setEsclationStatus(res);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPrioritySupport = async () => {
    console.log('status', userId, ticket);
    if (!isAuthenticated) {
      setOtpSent(true);
      await AuthServices.sendOTP(ticket?.rcn);
      setLoading(false);
      return;
    }

    if (userId !== ticket?.rcn) {
      return toast.error("This ticket doesn't belong to this account.");
    }

    let reportedOnMillis = new Date(reportedOn).getTime(),
      currentMillis = Date.now(),
      milliDiff = Math.abs(currentMillis - reportedOnMillis),
      daysDiff = Math.floor(milliDiff / DAY_MILLIS);

    if (daysDiff < 2) {
      return toast.error(
        `The priority support can only be requested after 48 hours of the reported time.`,
      );
    }
    setShowForm(true);
  };

  const handleSuccess = (msg: string) => {
    setShowForm(false);
    setMessage(msg);
    setShowMessage(true);
  };

  useEffect(() => {
    getEsclationStatus();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      {loading && <ActivityIndicator />}
      {esclationStatus?.status === 'true' ? (
        <EscalationStatusMessage data={esclationStatus?.data} />
      ) : showMessage ? (
        <EsclationResponseMessage message={message} />
      ) : showForm ? (
        <EsclationForm
          onSuccess={handleSuccess}
          show={showForm}
          setShow={setShowForm}
          ticket={ticket}
        />
      ) : isOtpSent ? (
        <OTPForm
          number={ticket?.rcn}
          handleRequestPrioritySupport={handleRequestPrioritySupport}
          setOtpSent={setOtpSent}
        />
      ) : (
        !loading && (
          <Button variant="contained" onClick={handleRequestPrioritySupport}>
            {isAuthenticated ? 'Escalate' : 'Verify to escalate'}
          </Button>
        )
      )}
    </>
  );
}
